import { Baseline, VisionStep } from '@wizeoo/akm-message-lib';
import { ViewData } from '../../model/view.data';
import { ChronocodeVO } from '../../vo/core/chronocode.vo';
import { FreedataValueVO } from  '../../vo/core/freedatavalue.vo';
import { IncExcKeyVO } from '../../vo/core/incexckey.vo';
import { IncExcContentObj } from '../../vo/core/tool/incex.content.obj';
import { BaselineEngine, VisionVO } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';

/**
 * génération des samples de lignes INC/ EXC
 */
export class IncExcSampleEngine {
  
  protected model: Model = null;
  constructor(model: Model) {
    this.model = model;
  }
  
  /* ################################################
            génération du sample de baselines
  ################################################ */

  public sampleBaselines: Map<string, Baseline> = null; // map de baselines "brutes", générées comme sample

  /**
   * construction des baselines potentielles sur la base des critères inc/exc de la vision en cours
   * @param vision vision cible, steps alimentés
   * @param incex critères d'exclusion
   * @returns la map des baselines, par level et code map[level] = {code = baseline}
   */
  public buildFakeBaselines(vision: VisionVO, incex: IncExcKeyVO[]): Map<number, Map<string, Baseline>> {
    const baselineMap: Map<number, Map<string, Baseline>> = new Map<number, Map<string, Baseline>>(); // map des baselines pour construction
    const visionKeyArr: string[] = Object.values(vision.visionKeys).sort((a, b) => a.order - b.order).map((item) => item.code); // collecte des clés de vision

    // map de gestion des inclusions. / exclusions. Inclusion ne vas pas générer les autres lignes pour la combinaison de champs parents
    // construction unique pour TOUS les levels. La comparaison se fera sur complétion de chaine |*|*|*... pour les 1er step
    // qui ne possèdent pas toutes les clés
    //
    // sous la forme FD = [valeur pour ce FD] = {collection de chaine avec wildcard }
    //
    // par exemple, pour une structure CHRONO#1, BRAND#1, GENDER#1
    // [BRAND] [1] = { *|1|3, *|1|2 } => la marque 1 est autorisée pour les genres 2 et 3
    // [BRAND] [7] = { *|7|5 } => la marque 7 est autorisée pour le genre 5
    //
    // si la vision était BRAND#1, CHRONO#1, GENDER#1 (on fait porter l'inclusion exclusion sur la clé la plus BASSE
    // [GENDER] [2] = { 2|*|1}
    // [GENDER] [3] = { 3|*|1}
    // [GENDER] [5] = { 3|*|7}
    const includeMap: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();
    const excludeMap: Map<string, Map<string, string[]>> = new Map<string, Map<string, string[]>>();

    this.buildIncludeExcludeMap(visionKeyArr, incex, includeMap, excludeMap);

    let previousStepBaselines: Map<string, Baseline> = null; // éléments du step précédent, qui serviront de précurseurs
    const allStepHierachical: Map<string, Map<string, string>> = new Map<string, Map<string, string>>(); // map clé(baseline) = map<part(sans level) = value> pour optim hiérarchie
    let previousStepKeySize: number = 0; // taille de la clé du step précédent, inutile de reconstruire les clés

    Object.values(vision.visionSteps)
      .sort((a, b) => a.level - b.level)
      .forEach((step: VisionStep) => {
        previousStepBaselines = this.buildStepLine(
          vision,
          step,
          includeMap,
          excludeMap,
          previousStepBaselines,
          allStepHierachical,
          previousStepKeySize
        );
        previousStepKeySize = BaselineEngine.getStepKeys(vision, step).length; //  step.getStepKeySize();

        baselineMap.set(step.level, previousStepBaselines);
      });

    return baselineMap;
  }

  /**
   * construction de la map d'inclusion (génération UNIQUEMENT si présent dans la MAP, pour une valeur d'axe). Construction de la map d'exclude
   * (suppression pure et simple de la clé)
   *
   * @param visionKeyArr, les clés de vision, ordonnées
   * @param incExcs       la collection d'inclusions / exclusions
   * @param includeMap à générer
   * @param excludeMap à générer
   */
  private buildIncludeExcludeMap(visionKeyArr: string[], incex: IncExcKeyVO[], includeMap: Map<string, Map<string, string[]>>, excludeMap: Map<string, Map<string, string[]>> ): void {
    
    if (!incex) { // pas de clés
      return;
    }

    incex.forEach((key: IncExcKeyVO) => {
      let entryPoint: string = null; // le champ le plus haut dans la pile de clé de la vision
      let entryValue: string = null; // la valeur pour ce champ

      // construction de la map de valeurs pour cette includeKey
      const wildParts: string[] = [];
      const keyParts: Map<string, IncExcContentObj> = key.keyMap;

      visionKeyArr.forEach((visionKey: string) => {
        if (keyParts.has(visionKey)) {
          if (!keyParts.get(visionKey)) {
            console.log('what', keyParts, visionKey);
          }
          wildParts.push(keyParts.get(visionKey).valuePart);
          // identifier le point d'enregistrement (le champ le plus BAS. C'est lui la vrai contrainte)
          entryPoint = visionKey;
          entryValue = keyParts.get(visionKey).valuePart;
        } else {
          wildParts.push(ViewData.wildcardDomain);
        }
      });

      let whosIs: Map<string, Map<string, string[]>> = null; // dans quelle Map
      if (key.inc) {
        // includeKey
        whosIs = includeMap;
      } // excludeKey
      else {
        whosIs = excludeMap;
      }

      if (!whosIs.has(entryPoint)) {
        // et ajout à la map
        whosIs.set(entryPoint, new Map<string, string[]>());
      }
      if (!whosIs.get(entryPoint).has(entryValue)) {
        whosIs.get(entryPoint).set(entryValue, []);
      }
      whosIs.get(entryPoint).get(entryValue).push(wildParts.join(ViewData.KEY_SEPARATOR));
    });
  }

  /**
   * construction des baselines du step
   *
   * @param vision vision en cours
   * @param step                  le step
   * @param includeMap            la map d'inclusions
   * @param excludeMap            la map d'exclusions
   * @param previousStepBaselines les Baselines générées lors du step précédent
   * @param allStepHierachical code des baselines du niveau précédent, avec map des valeurs par code FD/CHRONO sans level pour check hiérarchie
   * @param previousStepKeySize   le nombre de clé déjà consommées par le step précédent
   * @return la map <key = Baseline> pour ce step
   */
  public buildStepLine(
    vision: VisionVO,
    step: VisionStep,
    includeMap: Map<string, Map<string, string[]>>,
    excludeMap: Map<string, Map<string, string[]>>,
    previousStepBaselines: Map<string, Baseline>,
    allStepHierachical: Map<string, Map<string, string>>,
    previousStepKeySize: number
  ): Map<string, Baseline> {
    // collecte de la liste (ordonnée) des clés du step
    
    let stepKeys: string[] = BaselineEngine.getStepKeys(vision, step).map(item => item.code);
    // let stepKeys: string[] = [...step.getStepKeys().keys()];
    stepKeys = stepKeys.slice(previousStepKeySize, stepKeys.length); // on ne prend pas les clés déjà utilisée

    const stepKeyValues: Map<string, string[]> = new Map<string, string[]>(); // pour chaque clé, collecte de l'ensemble des valeurs
    let chronos: ChronocodeVO[];
    let fdvs: FreedataValueVO[];
    stepKeys.forEach((stepKey: string) => {
      // pour chaque clé, on collecte l'ensemble des valeurs du FD / Chrono (par level, peu importe la dépendance à ce stade)
      const splittedKey: string[] = stepKey.split(ViewData.CONTEXT_SEPARATOR);
      if (ViewData.CHRONO === splittedKey[0]) {
        // CHRONO
        let chrono: ChronocodeVO = this.model.chronocodeEngine.getChronocode(vision.chronoCode);
        chronos = this.model.chronocodeEngine.flatChronoFamily(chrono).filter(
          (chro) => chro.level === Number(splittedKey[1])
        );
        stepKeyValues.set(
          stepKey,
          chronos.map((chro) => chro.code)
        );
      } // FD
      else {
        // prise du bon FD
        fdvs = this.model.freedataEngine.getFreedataValuesByLevel(stepKey);
        stepKeyValues.set(
          stepKey,
          fdvs.map((fdv) => fdv.code)
        );
      }
    });

    // et on build en tenant compte des inclusions
    return this.buildLine(
      vision,
      step,
      stepKeys,
      stepKeyValues,
      includeMap,
      excludeMap,
      previousStepBaselines,
      allStepHierachical,
      previousStepKeySize
    );
  }

  /**
   * construction des lignes
   *
   * @param vision source
   * @param visionStep step en cours
   * @param stepKeys              les clés du step, ordonnées
   * @param stepKeyValues         les valeurs possibles pour chaque clé de step
   * @param includeMap            la map d'inclusions
   * @param excludeMap            la map d'exclusions
   * @param previousStepBaselines les Baselines générées lors du step précédent
   * @param allStepHierachical    code des baselines du niveau précédent, avec map des valeurs par code FD/CHRONO sans level pour check hiérarchie
   * @param previousStepKeySize   le nombre de clé déjà consommées par le step précédent
   * @return les lignes générées
   */
  private buildLine(
    vision: VisionVO,
    step: VisionStep,
    stepKeys: string[],
    stepKeyValues: Map<string, string[]>,
    includeMap: Map<string, Map<string, string[]>>,
    excludeMap: Map<string, Map<string, string[]>>,
    previousStepBaselines: Map<string, Baseline>,
    allStepHierachical: Map<string, Map<string, string>>,
    previousStepKeySize: number
  ): Map<string, Baseline> {
    let precursors: string[] = null; // les chaines en cours de construction
    if (previousStepBaselines != null && previousStepBaselines.size > 0) {
      // alimentation avec le step précédent
      precursors = Array.from(previousStepBaselines.keys());
    }

    let newLines: Map<string, Map<string, string>> = new Map<string, Map<string, string>>(); // les chaines en cours de construction, et leur map par FD/CHRONO sans level

    stepKeys.forEach((stepKey: string) => {
      // on dépile les clés de step, dans l'ordre
      const valueCandidates: string[] = stepKeyValues.get(stepKey); // toutes les clés pour un champ de step
      const stepKeyLevelFree: string = stepKey.split(ViewData.CONTEXT_SEPARATOR)[0]; // la clé de step sans son niveau
      valueCandidates.forEach((valueCandidate: string) => {
        if (!precursors || precursors.length < ViewData.MAX_STEP_LINES) {
          // limitation du nombre de lignes
          this.addLinePart(
            precursors,
            allStepHierachical,
            newLines,
            stepKey,
            stepKeyLevelFree,
            valueCandidate,
            includeMap,
            excludeMap
          ); // va construire les précurseurs
        }
      });
      precursors = Array.from(newLines.keys()); // les lignes générées deviennent les précurseurs
      newLines.forEach((value: Map<string, string>, key: string) => {
        allStepHierachical.set(key, value);
      });
      newLines = new Map<string, Map<string, string>>(); // et on boucle
    });

    return this.tranformToBaseline(vision, step, precursors, previousStepKeySize);
  }

  /**
   * @param precursors     les lignes des parts précédentes, si existe
   * @param allStepHierachical    code des baselines du niveau précédent, avec map des valeurs par code FD/CHRONO sans level pour check hiérarchie
   * @param newLines       les lignes en cours de création, avec leurs parts par FD/ CHRONO sans level
   * @param stepKey        la clé de champ en cours
   * @param stepKeyLevelFree        la clé de champ en cours sans le niveau
   * @param valueCandidate la valeur de champ en cours * précursors
   * @param includeMap     la map d'inclusions
   * @param excludeMap     la map d'exclusions
   */
  private addLinePart(
    precursors: string[],
    allStepHierachical: Map<string, Map<string, string>>,
    newLines: Map<string, Map<string, string>>,
    stepKey: string,
    stepKeyLevelFree: string,
    valueCandidate: string,
    includeMap: Map<string, Map<string, string[]>>,
    excludeMap: Map<string, Map<string, string[]>>
  ): void {
    if (precursors == null) {
      // premier step, aucune exclusion possible ici
      const map: Map<string, string> = new Map<string, string>();
      map.set(stepKeyLevelFree, valueCandidate);
      let hasError: boolean = false; // construction impossible (mauvaise hiérarchie, include / exclude)

      // contrôle de cohérence dès le 1er step
      if (!hasError && includeMap?.has(stepKey)) {
        // il existe une inclusion pour ce champ
        
        let allStepKeys: string[] = []; // on doit prendre toutes les valeurs.. je suis actif si je suis présent 1 fois à minima
        [...includeMap.get(stepKey).values()].forEach((keyMap) => {
          allStepKeys = allStepKeys.concat(keyMap);
          // console.log('keyMap', keyMap);
        });

        if (!this.inclusionOK(valueCandidate, allStepKeys)) {
          hasError = true;
        }
        // console.log('step1 inclusionOK', valueCandidate, hasError);
      }

      if (!hasError && excludeMap?.has(stepKey) && excludeMap.get(stepKey).has(valueCandidate)) {
        // il existe une exclusion pour ce champ
        if (this.inclusionOK(valueCandidate, excludeMap.get(stepKey).get(valueCandidate))) {
          // on inverse le résultat... inclusionOK sur les excludes, c'est ewclusionKO
          hasError = true;
        }
      }

      if (!hasError) {
        // console.log('step1 add', valueCandidate);
        newLines.set(valueCandidate, map);
      } else {
        //  console.log('step1 reject', valueCandidate);
      }


    } else {
      let newBornLine: string; // la ligne à créer
      let newPart: string; // le candidat complet, précurseur + clé en cours
      let hasError: boolean; // construction impossible (mauvaise hiérarchie, include / exclude)

      precursors.forEach((precursor: string) => {
        // pour chaque précurseur, on va ajouter la clé en cours

        if (newLines.size > ViewData.MAX_STEP_LINES) {
          // limitation du nombre de lignes
          return;
        }

        newPart = precursor + ViewData.KEY_SEPARATOR + valueCandidate; // ce qu'on cherche à créer (string complet)
        hasError = false; // jusqu'ici, tout va bien
        newBornLine = null; // la ligne qui veut vivre...

        // TODO optim. Mettre en clé binaire sur chaque parent (FD / Chrono) et faire la somme.
        // L'enfant doit contenir la valeur du parent (décalage de bit)
        if (allStepHierachical.get(precursor).has(stepKeyLevelFree)) {
          // contrôle de cohérence hiérarchique
          if (!valueCandidate.startsWith(allStepHierachical.get(precursor).get(stepKeyLevelFree))) {
            // me contient pas, mais parent hiérarchique
            hasError = true;
          }
        }



        if (!hasError && includeMap?.has(stepKey)) {//  && includeMap.get(stepKey).has(valueCandidate)) {
          
          let allStepKeys: string[] = []; // on doit prendre toutes les valeurs.. je suis actif si je suis présent 1 fois à minima
          [...includeMap.get(stepKey).values()].forEach((keyMap) => {
            allStepKeys = allStepKeys.concat(keyMap);
            // console.log('keyMap', keyMap);
          });
          
          // il existe une inclusion pour ce champ
          if (!this.inclusionOK(newPart, allStepKeys)) {
            hasError = true;
          }
        }

        if (!hasError && excludeMap?.has(stepKey) && excludeMap.get(stepKey).has(valueCandidate)) {
          // il existe une exclusion pour ce champ
          if (this.inclusionOK(newPart, excludeMap.get(stepKey).get(valueCandidate))) {
            // on inverse le résultat... inclusionOK sur les excludes, c'est ewclusionKO
            hasError = true;
          }
        }

        if (!hasError) {
          newLines.set(newPart, new Map(allStepHierachical.get(precursor)));
          newLines.get(newPart).set(stepKeyLevelFree, valueCandidate);
        }
      });
    }
  }

  /**
   * comparaison de chaine avec les includeKeys pour les inclusions / exclusion
   *
   * @param key           la clé en cours
   * @param availableKeys // les clés autorisées
   * @return true si match au MOINS 1 des expressions (donc OK pour inclusion, mais KO pour exclusion)
   */
  private inclusionOK(key: string, availableKeys: string[]): boolean {
    const splitKey: string[] = key.split(ViewData.KEY_SEPARATOR);

    let globalResult: boolean = false; // on considère que c'est niet
    const globalLen: number = splitKey.length; // la taille des chaines à tester

    const availableKeyLen: number = availableKeys.length; // nombre de clés à tester
    let availableIdx: number = 0; // clé de validité en cours de test

    while (availableIdx < availableKeyLen && globalResult === false) {
      // jusqu'a la fin des clés valides ou en cas de succès
      const availableKey: string = availableKeys[availableIdx];
      const splitAvailable: string[] = availableKey.split(ViewData.KEY_SEPARATOR); // fragmentation de la clé d'inclusion / exclusion

      let thatKeyResult: boolean = true; // retour de test, on considère que tout va bien
      let idx: number = 0; // index de la part de clé

      while (idx < globalLen && thatKeyResult === true) {
        // on essaye de boucler jusq'au bout sans erreur
        if (ViewData.wildcardDomain !== splitAvailable[idx] && splitKey[idx] !== splitAvailable[idx]) {
          thatKeyResult = false; // pas bon, on change de availableKey
        }
        idx++;
      }
      if (thatKeyResult === true) {
        // j'ai passé une boucle avec succès
        globalResult = true;
      }

      availableIdx++;
    }

    return globalResult;
  }

  /**
   * transformation des précurseurs en Baselines
   *
   * @param vision en cours
   * @param step en cours
   * @param precursors lignes à créer
   * @param previousStepKeySize le nombre de clé déjà consommées par le step précédent
   * @return la map des nouvelles lignes
   */
  private tranformToBaseline(
    vision: VisionVO,
    step: VisionStep,
    precursors: string[],
    previousStepKeySize: number): Map<string, Baseline> {
      
    const lines: Map<string, Baseline> = new Map<string, Baseline>(); // objet retour
    let bl: Baseline;
    let parentParts: string[];
    let parentKey: string;

    precursors.forEach((precursor: string) => {
      bl = new Baseline();
      bl.key = precursor;
      bl.domain = step.domain;
      bl.businessUnitCode = step.businessUnitCode;
      bl.chron = step.chronoCode;
      bl.v = vision.code;
      bl.vs = step.code;
      bl.level = step.level;
      bl.scen = ViewData.DEFAULT_SCENARIO;
      bl.values = {}; // on initialise les values, pour conformité avec formulaCoreEngine

      if (previousStepKeySize > 0) {
        // alimentation de la ligne parente
        parentParts = precursor.split(ViewData.KEY_SEPARATOR);
        parentParts = parentParts.slice(0, previousStepKeySize);
        parentKey = parentParts.join(ViewData.KEY_SEPARATOR);
        bl.pkey = parentKey;
      }
      lines.set(precursor, bl);
    });

    return lines;
  }
}
